import { SparklesIcon } from "lucide-react";
import { Link, Outlet } from "react-router";
import {
	AUTH_ENABLED,
	authClient,
	LOGIN_PATH,
	WORKSPACE_PATH,
} from "../lib/authClient";
import { chatUrl } from "../lib/chatHost";
import { cn } from "../lib/utils";
import LogoIcon from "./LogoIcon";
import { Button, buttonVariants } from "./ui/button";

const DEMO_BOOKING_URL = "https://cal.com/team/clerical/clerical-demo";

export default function Layout() {
	const session = authClient.useSession();
	const user = session.data?.user as
		| { name?: string; email?: string; image?: string }
		| undefined;
	const showSignedIn = AUTH_ENABLED && !session.isPending && !!user;

	async function handleSignOut() {
		await authClient.signOut();
	}

	return (
		<div className="min-h-screen bg-background text-foreground">
			<header className="bg-pine-forest">
				<nav className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
					<Link
						to="/"
						className="inline-flex items-center gap-2 text-cream transition-colors hover:text-cream-dark sm:gap-3"
					>
						<LogoIcon className="size-7 sm:size-9" />
						<span
							className="text-xl font-normal uppercase leading-none tracking-[0.08em] sm:text-[28px] sm:tracking-[0.1em]"
							style={{ fontFamily: "'Cinzel', serif" }}
						>
							Clerical
						</span>
					</Link>
					<div className="flex items-center gap-6">
						<a
							href="/#automate"
							className="hidden text-sm font-medium text-cream/70 transition-colors hover:text-cream sm:inline"
						>
							What We Automate
						</a>
						<a
							href="/#how"
							className="hidden text-sm font-medium text-cream/70 transition-colors hover:text-cream sm:inline"
						>
							How It Works
						</a>
						{showSignedIn && (
							<a
								href={chatUrl("/chat")}
								className={cn(
									buttonVariants({ variant: "outline", size: "sm" }),
									"hidden gap-1.5 border-cream/30 bg-transparent text-cream hover:bg-cream/10 hover:text-cream sm:inline-flex",
								)}
							>
								<SparklesIcon className="size-4" />
								Open Chat
							</a>
						)}
						{showSignedIn ? (
							<>
								<Link
									to={WORKSPACE_PATH}
									className={cn(
										buttonVariants({ variant: "outline", size: "sm" }),
										"border-cream/30 bg-transparent text-cream hover:bg-cream/10 hover:text-cream",
									)}
								>
									Dashboard
								</Link>
								<div className="flex items-center gap-3">
									<span className="text-sm text-cream/80">
										{user?.name || user?.email}
									</span>
									<Button
										variant="link"
										onClick={handleSignOut}
										className="h-auto p-0 text-sm text-cream/70 underline-offset-4 hover:text-cream"
									>
										Sign out
									</Button>
								</div>
							</>
						) : (
							<div className="flex items-center gap-3 sm:gap-4">
								<Link
									to={LOGIN_PATH}
									className="text-sm font-medium text-cream/70 transition-colors hover:text-cream"
								>
									Log in
								</Link>
								<a
									href={DEMO_BOOKING_URL}
									className={cn(
										buttonVariants({ size: "sm" }),
										"bg-cream text-pine-forest hover:bg-cream-dark!",
									)}
								>
									Get Started
								</a>
							</div>
						)}
					</div>
				</nav>
			</header>

			<main>
				<Outlet />
			</main>

			<footer className="bg-pine-forest">
				<div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
					<Link
						to="/"
						className="inline-flex items-center gap-3 text-cream transition-colors hover:text-cream-dark"
					>
						<LogoIcon className="size-8" />
						<span
							className="text-[24px] font-normal uppercase leading-none tracking-[0.1em]"
							style={{ fontFamily: "'Cinzel', serif" }}
						>
							Clerical
						</span>
					</Link>
					<div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-cream/60">
						<Link to="/privacy" className="transition-colors hover:text-cream">
							Privacy
						</Link>
						<Link to="/terms" className="transition-colors hover:text-cream">
							Terms of Service
						</Link>
						<Link to="/support" className="transition-colors hover:text-cream">
							Support
						</Link>
						<Link
							to="/changelog"
							className="transition-colors hover:text-cream"
						>
							Changelog
						</Link>
						<a
							href="mailto:support@clerical.com"
							className="transition-colors hover:text-cream"
						>
							Contact
						</a>
						<span>&copy; 2026 Clerical</span>
					</div>
				</div>
			</footer>
		</div>
	);
}
